import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LaravelResourceResponse } from '../../../../../../../../_base-shared/contracts/laravel-response.interface';
import { Case } from '../../../../../../../../_base-shared/models/Case/Case';
import { CaseCreditor } from '../../../../../../../../_base-shared/models/Case/CaseCreditor';
import { PaymentTerm } from '../../../../../../../../_base-shared/models/Payment/PaymentTerm';
import { User } from '../../../../../../../../_base-shared/models/User/User';
import { environment } from '../../../../../../environments/environment';
import { MainGlobalEventService } from '../../../../../_shared/services/main-global-event.service';
import { PaymentService } from '../../../../payment/payment.service';
import { CaseCreditorService } from '../../../case-creditor.service';
import { CaseService } from '../../../case.service';

@Component({
  standalone: false,
  selector:    'app-case-creditor-overview',
  templateUrl: './case-creditor-overview.component.html',
  styleUrls:   ['./case-creditor-overview.component.scss']
})
export class CaseCreditorOverviewComponent implements OnInit, OnDestroy {
  @Input() case: Case;
  @Input() caseCreditor: CaseCreditor;
  @Output() caseClientsUpdated: EventEmitter<Case> = new EventEmitter<Case>();
  @Output() settlementBalanceChanged: EventEmitter<number> = new EventEmitter<number>();
  public loadRelations                             = [
    'lead', 'client.address', 'partner.address', 'linked_by_cases.client.address', 'linked_cases.client.address',
  ];
  public isLoading                                 = 0;
  public form: UntypedFormGroup;
  public serverResponse: LaravelResourceResponse;
  public isEditing                                 = false;
  public isSubmitting                              = false;
  public authUser: User;
  public settlementBalance: number;
  public debtsSettledAmount: number;
  public totalContribution: number;
  public settledCreditors: Array<CaseCreditor>;
  public isFetching                              = 0;
  public loanCreditors: Array<CaseCreditor>;

  protected subscriptions: Array<Subscription> = [];

  constructor(
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private translate: TranslateService,
    private caseService: CaseService,
    private globalEventsService: MainGlobalEventService,
    private paymentService: PaymentService,
    private caseCreditorService: CaseCreditorService,
  ) {
  }

  ngOnInit(): void {
    this.fetchCaseCreditors();
    this.globalEventsService.authUser$.subscribe(user => {
      this.authUser = user;
      this.buildForm(this.caseCreditor);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
  }

  public isElementRestricted(): boolean {
    const enabledRoles         = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId      = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (
      !enabledStatusId.includes(this.case.status_id) ||
      enabledRoles.includes(this.authUser.role_id) ||
      isDepartmentAllowed
    ) {
      return true;
    }
    return false;
  }

  public toggleEditMode(): void {
    this.isEditing = !this.isEditing;
    this.toggleFormControls(this.isEditing);
  }

  public submitForm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const requestData = this.form.value;
    if (!requestData.client.address?.address_1) {
      requestData.client.address.address_1 = null;
    }
    if (this.case.joint_application && !requestData.partner?.address?.address_1) {
      requestData.partner.address.address_1 = null;
    }

    this.isSubmitting = true;
    this.caseService.update(this.case.id, requestData, this.loadRelations)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe(
        result => {
          this.toastr.success(
            this.translate.instant('CASES.editor.general.result.update.success'),
            this.translate.instant('SHARED.success'),
          );
          const updatedCase = result.data;
          this.case.lead    = updatedCase.lead;
          this.case.client  = updatedCase.client;
          this.case.partner = updatedCase.partner;
          this.toggleEditMode();
        },
        err => this.serverResponse = err.error,
      );
  }

  private toggleFormControls(isEnabled: boolean): void {

    const enabledRoles         = environment.ALLOW_CASES_ROLES_IDS;
    const enabledStatusId      = environment.BLOCK_CASES_STATUS_IDS;
    const enabledDepartmentsId = environment.ALLOW_CASES_DEPARTMENTS_IDS;

    const isDepartmentAllowed = this.authUser.departments.some(department =>
      enabledDepartmentsId.includes(department.id)
    );

    if (isEnabled) {
      if (
        !enabledStatusId.includes(this.case.status_id) ||
        enabledRoles.includes(this.authUser.role_id) ||
        isDepartmentAllowed
      ) {
        this.form.get('negotiated_amount').enable();
        /*   if (this.case.joint_application) {
             this.form.get('partner.first_name').enable();
             this.form.get('partner.last_name').enable();
             this.form.get('partner.email').enable();
             this.form.get('partner.phone').enable();
             this.form.get('partner.home_phone').enable();
             this.form.get('partner.work_phone').enable();
             this.form.get('partner.id_card').enable();
             this.form.get('partner.debt_aware').enable();
             this.form.get('partner.vulnerability').enable();
             this.form.get('partner.address.address_1').enable();
             this.form.get('partner.address.address_2').enable();
           }*/
      }
    } else {
      this.form.get('negotiated_amount').disable();
      /* if (this.case.joint_application) {
         this.form.get('partner.first_name').disable();
         this.form.get('partner.last_name').disable();
         this.form.get('partner.email').disable();
         this.form.get('partner.phone').disable();
         this.form.get('partner.home_phone').disable();
         this.form.get('partner.work_phone').disable();
         this.form.get('partner.id_card').disable();
         this.form.get('partner.debt_aware').disable();
         this.form.get('partner.vulnerability').disable();
         this.form.get('partner.address.address_1').disable();
         this.form.get('partner.address.address_2').disable();
       }*/
    }
  }

  private buildForm(caseCreditor: CaseCreditor): void {
    console.log(caseCreditor);
    this.form = this.fb.group({
      ref_number:                   [{value: caseCreditor.reference_number, disabled: true}],
      creditor_name:                [{value: caseCreditor.creditor?.name, disabled: true}],
      initial_balance:              [{value: caseCreditor.initial_balance, disabled: true}],
      negotiated_amount:            [{value: caseCreditor.negotiated_amount, disabled: true}],
      reduction:                    [{
        value:    caseCreditor.initial_balance - caseCreditor.negotiated_amount,
        disabled: true
      }],
      negotiated_amount_updated_at: [{value: caseCreditor.negotiated_amount_updated_at, disabled: true}],
      days_in_status:               [{value: null, disabled: true}],
      available_funds:              [{value: null, disabled: true}],
      expected_fee:                 [{
        value:    (caseCreditor.initial_balance - caseCreditor.negotiated_amount) * 0.25,
        disabled: true
      }],
    });

    const daysInStatus = this.getDaysInStatus(caseCreditor);
    this.form.get('days_in_status').patchValue(daysInStatus);
    this.form.get('available_funds').patchValue(this.settlementBalance);
  }

  public getDaysInStatus(element): number {
    if (element.latest_case_creditor_status_log && element.latest_case_creditor_status_log.created_at) {
      return Math.ceil(DateTime.fromISO(element.latest_case_creditor_status_log.created_at).diffNow('days').negate().as('days'));
    }
  }

  public calculateDebtSettlementAmount(caseCreditors: Array<CaseCreditor>): void {
    this.settledCreditors   = caseCreditors.filter(caseCreditor => {
      return (
        caseCreditor.case_creditor_status?.slug === 'ds_settled' &&
        caseCreditor.product?.slug !== 'exclude' &&
        !(caseCreditor.creditor?.lender_type === 'bank' &&
          caseCreditor.creditor?.accept_debt_settlement === false
        )
      );
    });
    this.debtsSettledAmount = this.settledCreditors.reduce((sum, item) => {
      return sum + (+item.negotiated_amount || 0);
    }, 0);
    console.log(this.totalContribution);
    console.log(this.debtsSettledAmount);
    this.fetchDebtPlanFees(this.case.id);
  }

  private fetchDebtPlanFees(caseId: number): void {
    const data = {
      case_id:                caseId,
      select_all:             0,
      payment_plan_type_slug: 'debt_plan_fee',
    };
    this.isLoading++;
    this.paymentService.getInstallmentFees(caseId, data, [])
      .pipe(finalize(() => this.isLoading--))
      .subscribe(
        (result: LaravelResourceResponse<Array<PaymentTerm>>) => {
          this.totalContribution = result.data.reduce((sum, item) => {
            return sum + (item.amount_paid || 0);
          }, 0);
          this.settlementBalance = this.totalContribution - this.debtsSettledAmount;
          this.form.get('available_funds').patchValue(this.settlementBalance);
          this.settlementBalanceChanged.emit(this.settlementBalance);
          },
        err => console.error(err),
      );
  }

  private fetchCaseCreditors(): void {
    this.isFetching++;
    const relations    = [];

    this.caseCreditorService.getCaseCreditors(this.case.id, {type: 'unsecured'}, relations)
      .pipe(finalize(() => this.isFetching--))
      .subscribe(result => {
        this.loanCreditors   = result.data;
        this.calculateDebtSettlementAmount(this.loanCreditors );
      });
  }
}
